import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import Button from '../../components/common/Button'
import AdvisingDiagnostic from '../../components/home/AdvisingDiagnostic'
import AcademicPathwayViewer from '../../components/home/AcademicPathwayViewer'
import JobMarketInsights from '../../components/home/JobMarketInsights'
import HomeStats from '../../components/home/HomeStats/HomeStats'
import axiosInstance from '../../services/axiosInstance'
import { Sparkles, GraduationCap, Briefcase } from 'lucide-react'
import './styles.scss'
import { getYear } from '../../utils/dateUtils'

const FEATURES = [
  {
    icon: '📊',
    title: 'Workforce Analytics',
    description: 'Visualize regional workforce demand, education supply, NAICS industry wage breakdowns, and gap analysis benchmarks.',
    path: '/workforce-analytics',
    requiresAuth: true
  },
  {
    icon: '🗂️',
    title: 'Datasets',
    description: 'Browse, search, and manage institutional datasets. Upload spreadsheets and explore structured academic data tables.',
    path: '/datasets',
    requiresAuth: true
  },
  {
    icon: '🎓',
    title: 'Academic Hub',
    description: 'Access the academic knowledge base — program mappings, course cross-references, and institutional intelligence resources.',
    path: '/academic-hub',
    requiresAuth: true
  }
]

export default function Home() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const [institution, setInstitution] = useState(localStorage.getItem('preferred_institution') || '')
  const [institutionName, setInstitutionName] = useState(localStorage.getItem('preferred_institution_name') || '')
  const [availableInstitutions, setAvailableInstitutions] = useState([]);
  const [activeTab, setActiveTab] = useState('diagnostic') // 'diagnostic', 'pathway', or 'market'

  useEffect(() => {
    const fetchAvailable = async () => {
      try {
        const response = await axiosInstance.get('/api/advising/institutions');
        if (response.data && Array.isArray(response.data) && response.data.length > 0) {
          setAvailableInstitutions(response.data);
        }
      } catch (err) {
        console.error('Failed to fetch available institutions:', err);
      }
    };
    fetchAvailable();
  }, []);

  useEffect(() => {
    const fetchMyInstitution = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const queryInst = urlParams.get('inst');
        const apiPath = queryInst ? `/api/advising/my-institution?inst=${queryInst}` : '/api/advising/my-institution';

        const response = await axiosInstance.get(apiPath);
        if (response.data) {
          if (response.data.shortName && !response.data.unresolved) {
            setInstitution(response.data.shortName);
            setInstitutionName(response.data.fullName || response.data.shortName);
            localStorage.setItem('preferred_institution', response.data.shortName);
            localStorage.setItem('preferred_institution_name', response.data.fullName || response.data.shortName);
          } else {
            const savedInst = localStorage.getItem('preferred_institution');
            const savedName = localStorage.getItem('preferred_institution_name');
            if (savedInst) {
              setInstitution(savedInst);
              setInstitutionName(savedName || savedInst);
            } else {
              // Default guest fallback to University of Memphis (UOM)
              setInstitution('UOM');
              setInstitutionName('University of Memphis');
              localStorage.setItem('preferred_institution', 'UOM');
              localStorage.setItem('preferred_institution_name', 'University of Memphis');
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch user institution:', err);
        const savedInst = localStorage.getItem('preferred_institution');
        const savedName = localStorage.getItem('preferred_institution_name');
        if (savedInst) {
          setInstitution(savedInst);
          setInstitutionName(savedName || savedInst);
        } else {
          // Default guest fallback to University of Memphis (UOM)
          setInstitution('UOM');
          setInstitutionName('University of Memphis');
          localStorage.setItem('preferred_institution', 'UOM');
          localStorage.setItem('preferred_institution_name', 'University of Memphis');
        }
      }
    };
    fetchMyInstitution();
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <div className="home-container">

      {/* Hero Section */}
      <section className="home-hero">
        <div className="hero-glow" />
        <div className="hero-content">
          <div className="hero-badge" style={{ display: 'none' }}>Powered by Linqra</div>
          <h1 className="hero-title">
            <span className="hero-title-bold">Smart</span>
            <span className="hero-title-light">Advising</span>
          </h1>
          <p className="hero-subtitle">
            Ask our AI Advisor about your degree pathways, credit transfers, and career alignment.
          </p>
        </div>
      </section>

      {/* Stats and Diagnostic Assistant Section */}
      <section id="diagnostic-assistant" className="home-diagnostic-section">
        {!institution ? (
          <div className="institution-onboarding-card">
            <h2 className="onboarding-title">Select Your Institution to Begin</h2>
            <p className="onboarding-subtitle">
              Choose your target university to access optimized BAS degree pathways, transfer metrics, and local career salaries.
            </p>
            <div className="onboarding-grid">
              {availableInstitutions.map((inst) => (
                <button
                  key={inst.shortName || inst}
                  className="onboarding-inst-btn"
                  onClick={() => {
                    setInstitution(inst.shortName);
                    setInstitutionName(inst.fullName || inst.shortName);
                    localStorage.setItem('preferred_institution', inst.shortName);
                    localStorage.setItem('preferred_institution_name', inst.fullName || inst.shortName);
                  }}
                >
                  <GraduationCap className="onboarding-inst-icon" size={32} />
                  <span className="onboarding-inst-name">{inst.fullName || inst.shortName}</span>
                  <span className="onboarding-inst-badge">Active Pathway</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            <HomeStats
              institution={institution}
              setInstitution={(val) => {
                setInstitution(val);
                const matched = availableInstitutions.find(i => (i.shortName || i) === val);
                if (matched) {
                  setInstitutionName(matched.fullName || matched.shortName || val);
                  localStorage.setItem('preferred_institution', matched.shortName || val);
                  localStorage.setItem('preferred_institution_name', matched.fullName || matched.shortName || val);
                } else {
                  setInstitutionName(val);
                  localStorage.setItem('preferred_institution', val);
                  localStorage.setItem('preferred_institution_name', val);
                }
              }}
              institutionName={institutionName}
              availableInstitutions={availableInstitutions}
            />

            <div className="home-tabs-container">
              <div className="tabs-switcher-glass">
                <button
                  className={`tab-switch-btn ${activeTab === 'diagnostic' ? 'active' : ''}`}
                  onClick={() => setActiveTab('diagnostic')}
                >
                  <Sparkles size={18} />
                  <span>AI Advisor</span>
                </button>
                <button
                  className={`tab-switch-btn ${activeTab === 'pathway' ? 'active' : ''}`}
                  onClick={() => setActiveTab('pathway')}
                >
                  <GraduationCap size={18} />
                  <span>Pathway Explorer</span>
                </button>
                <button
                  className={`tab-switch-btn ${activeTab === 'market' ? 'active' : ''}`}
                  onClick={() => setActiveTab('market')}
                >
                  <Briefcase size={18} />
                  <span>Job & Salaries</span>
                </button>
              </div>
            </div>

            <div className="tab-content-area">
              {activeTab === 'diagnostic' && (
                <AdvisingDiagnostic
                  institution={institution}
                  displayName={institutionName}
                />
              )}
              {activeTab === 'pathway' && (
                <AcademicPathwayViewer
                  institution={institution}
                  displayName={institutionName}
                />
              )}
              {activeTab === 'market' && (
                <JobMarketInsights
                  institution={institution}
                  displayName={institutionName}
                />
              )}
            </div>
          </>
        )}
      </section>

      {/* Features Section - Disabled for now
      <section id="platform-features" className="home-features-section">
        <div className="home-features-grid">
          <div className="features-header">
            <h2 className="features-title">Platform Features</h2>
            <p className="features-subtitle">
              Everything you need to analyze and manage academic data
            </p>
          </div>
          {FEATURES.map((feature, index) => (
            <div
              key={feature.title}
              className="home-feature-card"
              style={{ animationDelay: `${index * 0.1}s` }}
              onClick={() => {
                if (feature.requiresAuth && !isAuthenticated) {
                  navigate('/login')
                } else {
                  navigate(feature.path)
                }
              }}
            >
              <div className="home-feature-icon">{feature.icon}</div>
              <h3 className="home-feature-card-title">{feature.title}</h3>
              <p className="home-feature-card-description">{feature.description}</p>
              <span className="home-feature-link">
                {feature.requiresAuth && !isAuthenticated ? 'Sign in to access →' : 'Explore →'}
              </span>
            </div>
          ))}
        </div>
      </section>
      */}

      {/* Footer */}
      <footer className="home-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <img src="/smartadvising.svg" alt="Smart Advising" className="footer-logo" />
            <span className="footer-name">Smart Advising</span>
          </div>
          <div className="footer-powered-by" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', color: '#718096', marginTop: '0.5rem' }}>
            <span>Powered by</span>
            <a href="https://linqra.com" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', fontWeight: 600, color: '#ed7534', textDecoration: 'none' }}>
              <img src="/linqra.svg" alt="Linqra" style={{ width: '1.1rem', height: '1.1rem', verticalAlign: 'middle' }} />
              <span>Linqra.com</span>
            </a>
          </div>
          <p className="footer-copy" style={{ marginTop: '0.75rem' }}>
            &copy; {getYear()} Smart Advising &bull; Academic Intelligence Platform
          </p>
        </div>
      </footer>
    </div>
  )
}
